import { useEffect, useMemo, useState } from "react";
import { Button } from "@components/atoms";
import { getEventPaymentDiscounts } from "@services/discount.service";
import { formatCurrency } from "@shared/utils";
import type { Locality } from "@/types";

interface PurchaseSummaryProps {
  localities: Locality[];
  selections: Record<string, number>;
  eventId: number | string;
  onPay: () => void;
}

function Fila({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <span className="text-neutral-90" style={{ fontSize: 13 }}>
        {label}
      </span>
      <span className="text-primary-110" style={{ fontSize: 13 }}>
        {value}
      </span>
    </div>
  );
}

/**
 * seleccion-localidades-inline RF-7 / RF-7.1 — resumen tipo factura, a la derecha
 * de las localidades dentro de la sección de selección. Por debajo de `xl` manda
 * la barra fija (`PurchaseBar`), porque ahí no hay ancho para dos columnas.
 */
export function PurchaseSummary({
  localities,
  selections,
  eventId,
  onPay,
}: PurchaseSummaryProps) {
  const items = useMemo(
    () =>
      localities
        .filter((loc) => (selections[loc.id] ?? 0) > 0)
        .map((loc) => {
          const qty = selections[loc.id] ?? 0;
          return {
            id: loc.id,
            name: loc.name,
            qty,
            subtotal: qty * loc.price,
            commission: qty * loc.comision,
          };
        }),
    [localities, selections],
  );

  const subtotal = items.reduce((s, i) => s + i.subtotal, 0);
  const commission = items.reduce((s, i) => s + i.commission, 0);
  const hayEntradas = items.length > 0;

  // RF-7.2: el descuento se resuelve al elegir método de pago, así que el total de
  // acá no es final en los eventos que lo tienen. Se consulta recién con selección.
  const [tieneDescuentos, setTieneDescuentos] = useState(false);
  useEffect(() => {
    if (!hayEntradas) return;
    let vigente = true;
    getEventPaymentDiscounts(eventId)
      .then((descuentos) => {
        if (vigente) setTieneDescuentos(descuentos.some((d) => d.percentage > 0));
      })
      .catch(() => {});
    return () => {
      vigente = false;
    };
  }, [eventId, hayEntradas]);

  if (!hayEntradas) return null;

  return (
    // El panel es el que se adapta, no las tarjetas: con h-full se estira para
    // cerrar a la par de la lista cuando esta es más alta, o sea desde la cuarta
    // localidad. Con tres o menos se queda en su piso y sobresale, que es
    // preferible a deformar las tarjetas.
    //
    // El min-h de 228px es el alto de tres filas (3 × 68 + 2 × 12 de gap) y hoy
    // no llega a actuar, porque el contenido del panel ya mide ~289px. Está como
    // guarda: si alguien lo compacta más, no debe bajar de tres filas.
    <div className="flex h-full min-h-[228px] flex-col rounded-2xl bg-white p-4 shadow-lg">
      <span className="block font-semibold text-primary-110" style={{ fontSize: 16 }}>
        Resumen de compra
      </span>

      <div className="my-3 flex flex-col gap-1.5 border-y border-neutral-100 py-3">
        {items.map((item) => (
          <div key={item.id} className="flex items-baseline justify-between gap-4">
            <span className="min-w-0 flex-1 break-words text-primary-110" style={{ fontSize: 13 }}>
              {item.name} <span className="text-neutral-90">× {item.qty}</span>
            </span>
            <span className="shrink-0 text-primary-110" style={{ fontSize: 13 }}>
              {formatCurrency(item.subtotal)}
            </span>
          </div>
        ))}
      </div>

      <div className="flex flex-col gap-2">
        <Fila label="Subtotal" value={formatCurrency(subtotal)} />
        <Fila label="Impuestos" value={formatCurrency(commission)} />
      </div>

      <div className="mt-3 flex items-baseline justify-between gap-4 border-t border-neutral-100 pt-3">
        <span className="font-semibold text-primary-110" style={{ fontSize: 14 }}>
          Total
        </span>
        <span className="font-bold text-primary-110" style={{ fontSize: 22 }}>
          {formatCurrency(subtotal + commission)}
        </span>
      </div>

      {/* RF-5 (checkout-sin-paso-detalle) — antes decía "se aplica en el siguiente paso", y ese
          paso era el detalle de la compra, que se eliminó. El descuento se sigue aplicando: lo
          calcula el backend y el monto final se ve en la pasarela. */}
      {tieneDescuentos && (
        <p className="mt-2 leading-snug text-neutral-90" style={{ fontSize: 11 }}>
          El descuento por método de pago se aplica al pagar.
        </p>
      )}

      {/* mt-auto: el botón queda al pie, alineado con la última tarjeta de localidad. */}
      <div className="mt-auto shrink-0 pt-3">
        <Button
          className="h-[46px] w-full bg-secondary-120 text-primary-110 hover:bg-secondary-120 hover:opacity-90"
          onClick={onPay}
        >
          Pagar
        </Button>
      </div>
    </div>
  );
}
